import { Trophy, Instagram, Facebook, Mail, MapPin, Heart, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

// X (Twitter) icon
const XIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

export default function Footer() {
  const currentYear = new Date().getFullYear();
  
  const footerLinks = {
    navigation: [
      { label: "Maratontabell", href: "/tabell" },
      { label: "Jämför lag", href: "/jamfor" },
      { label: "Nyheter", href: "/nyheter" },
      { label: "Bloggen", href: "/blogg" },
    ],
    info: [
      { label: "Om Fotbollsliv", href: "/om" },
      { label: "Annonsera", href: "/annonsera" },
      { label: "Integritetspolicy", href: "/integritetspolicy" },
    ],
  };

  const socialLinks = [
    { 
      name: "Instagram", 
      icon: Instagram, 
      href: "https://www.instagram.com/fotbollsliv.com_/",
      color: "bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#FCAF45]"
    },
    { 
      name: "Facebook", 
      icon: Facebook, 
      href: "https://www.facebook.com/profile.php?id=61588288235618",
      color: "bg-[#1877F2]"
    },
    { 
      name: "X", 
      icon: XIcon, 
      href: "https://twitter.com/Fotbollslivcom",
      color: "bg-black"
    },
  ];

  const divisions = [
    { name: "Allsvenskan", count: 16 },
    { name: "Superettan", count: 16 },
    { name: "Div 1-5", count: "1000+" },
  ];

  return (
    <footer className="relative bg-gradient-to-b from-slate-900 to-slate-950 text-white mt-auto overflow-hidden" data-testid="footer">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#FECC00]/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      {/* Top accent line */}
      <div className="h-1 bg-gradient-to-r from-[#0053A0] via-[#FECC00] to-[#0053A0]" />

      {/* Main Footer */}
      <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 md:gap-8">
          
          {/* Brand Section */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-flex items-center gap-3 mb-5 group">
              <div className="w-10 h-10 bg-gradient-to-br from-[#FECC00] to-[#E5B800] rounded-xl flex items-center justify-center shadow-lg shadow-[#FECC00]/20 group-hover:shadow-[#FECC00]/40 transition-shadow">
                <Trophy className="w-5 h-5 text-slate-900" />
              </div>
              <span className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                FOTBOLLSLIV
              </span>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6 max-w-xs">
              Sveriges mest kompletta fotbollsranking. Följ alla lag från Allsvenskan ner till Division 5 i vår unika Super-maratontabell.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 rounded-xl ${social.color} flex items-center justify-center text-white shadow-lg hover:scale-110 hover:shadow-xl transition-all duration-200`}
                  data-testid={`social-${social.name.toLowerCase()}`}
                  title={social.name}
                >
                  <social.icon />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#FECC00] mb-5">
              Utforska
            </h3>
            <ul className="space-y-3">
              {footerLinks.navigation.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href} 
                    className="text-sm text-slate-400 hover:text-white hover:translate-x-1 transition-all duration-200 inline-block"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Info Links */}
          <div className="lg:col-span-2">
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#FECC00] mb-5">
              Information
            </h3>
            <ul className="space-y-3">
              {footerLinks.info.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href} 
                    className="text-sm text-slate-400 hover:text-white hover:translate-x-1 transition-all duration-200 inline-block"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link 
                  to="/kontakt"
                  className="text-sm text-slate-400 hover:text-white hover:translate-x-1 transition-all duration-200 inline-block"
                >
                  Kontakt
                </Link>
              </li>
            </ul>
          </div>

          {/* Stats & Contact */}
          <div className="lg:col-span-4">
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#FECC00] mb-5">
              Statistik
            </h3>
            
            {/* Mini stats */}
            <div className="grid grid-cols-3 gap-2 mb-6">
              {divisions.map((div) => (
                <div key={div.name} className="bg-slate-800/50 rounded-lg p-3 text-center border border-slate-700/50">
                  <div className="text-lg font-bold text-white">{div.count}</div>
                  <div className="text-xs text-slate-500">{div.name}</div>
                </div>
              ))}
            </div>

            {/* Contact info */}
            <div className="space-y-2">
              <a 
                href="mailto:kontakt@fotbollsliv.com" 
                className="flex items-center gap-2 text-sm text-slate-400 hover:text-[#FECC00] transition-colors"
              >
                <Mail className="w-4 h-4" />
                kontakt@fotbollsliv.com
              </a>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <MapPin className="w-4 h-4" />
                Sverige
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <p className="text-xs text-slate-500">
                &copy; {currentYear} Fotbollsliv.com
              </p>
              <span className="text-slate-700">•</span>
              <p className="text-xs text-slate-600">
                Data uppdateras dagligen
              </p>
            </div>
            <p className="text-xs text-slate-500 flex items-center gap-1.5">
              Skapat med <Heart className="w-3 h-3 text-red-500 fill-red-500 animate-pulse" /> för svensk fotboll
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
